// WAAP Settings — account-level settings page.
//
// Three account-wide feeds, all resolved through a representative (anchor) domain
// UUID — the `:id` only resolves the customer session, like the strategy /
// emergency reads. Backed by the PRIVATE portal (waap2) via eagle_waf/settings:
//
//   GET/PUT /waap/domains/:id/global-config   → portal /global_config
//   GET/PUT /waap/domains/:id/msi-email        → portal /msiEmail
//   GET     /waap/domains/:id/solution-detail  → portal /customer/solution/detail
//
// global-config is the Settings page's primary feed: it carries the Web Access
// Control IP-blacklist toggle, the Verified Bot user agents, the Response
// Filtering rules and the HTTP settings (max upload size, response-body
// inspection size, customised error pages). It is whole-object read-modify-write
// — a tab GETs the record, mutates the slice it owns and PUTs the WHOLE record
// back unchanged otherwise (use `withConfig` to patch). Responses are NOT
// enveloped (the BE unwraps the vendor `data`); an unconfigured resource is null.
//
// File management (IP-blacklist .txt, kill-switch / error-page HTML) reuses the
// shared static-files hooks (`useStaticFiles` / `useUploadStaticFile`), scoped by
// `type` (e.g. "global_balck_ip_list", "kill_switch").
//
// NOTE — PUBLIC Open-API account IP block list (kept PRIVATE, not migrated):
// A public surface exists at
//   GET/POST /waap/domains/:id/settings/ip-blacklist
// (BE: conversant.GetPublicIpBlacklist/UploadPublicIpBlacklist + openapi/account)
// exposing an INLINE string array `{ "ip_blacklist": ["1.2.3.0/24", …] }`
// (POST is set-then-replace; send the full list). That is conceptually the same
// "account IP block list" domain, but it does NOT map to how this feature works
// today: the private FE feature (WebAccessControlSettings.tsx) is FILE-based — an
// `enable_black_list` toggle in this global-config + an uploaded .txt managed via
// the static-files API ("global_balck_ip_list"); the IPs are never read into the
// UI as an array and there is no public equivalent for the enable toggle or the
// file/replace UX. The inline-array shape would instead match the unused
// `_shared.ts` `ip-lists` type (`WaapIPLists.blacklist`), which has NO FE consumer.
// Flipping this component to the public route would be a lossy rewrite, so the
// mapping is left UNCLEAR and this feature stays on the private surface.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3, dataCentralApi } from '@/api/client';

// ---- global_config DTOs (mirror the vendor wire shape) -------------------

/** Real-client-IP resolution strategy (Source IP). */
export interface SrcIpStrategy {
  src_ip_customed_name: string;
  src_ip_from_type: string; // e.g. "X-Forwarded-For"
  xff_position: string; // e.g. "first"
}

/** Verified-bot config (the "Verified Bot" tab). When `verified_by_ua_enabled`
 *  the edge forwards bots whose user agent matches an entry in `ua_list`. The
 *  vendor mirrors the textarea into `ua_list_str` (newline-joined) + `ua_list`. */
export interface GoodBots {
  verified_by_ua_enabled: boolean;
  ua_list_str: string;
  ua_list: string[];
}

/** One customised-error-page row (the "HTTP Settings" error-page table). The
 *  uploaded HTML is referenced by `file_id`; `enable_replace` swaps in the custom
 *  page for that status code. */
export interface ErrorPageEntry {
  code: number;
  file_name: string;
  /** Uploaded file id; "" when none uploaded yet. */
  file_id: number | string;
  file_name_display: string;
  enable_replace: boolean;
}

/** Direct-connection (origin protection) control — account IP allowlist tuples
 *  [ip, mask, label, kind]. Not surfaced as a Settings tab yet; round-tripped. */
export interface DirectConnectionControl {
  enable: boolean;
  ip_list: string[][];
}

/** Response-body inspection size (KB) — the "HTTP Settings" tab. */
export interface InspectionSetting {
  response_body_size: number;
}

/** One Response-Filtering rule (the "Response Filtering" tab): search & replace
 *  content in responses before returning to the client. Vendor wire shape — note
 *  `subfilter_is_regex` is a string flag ("1" = regex, "0" = plain). */
export interface ResponseFilteringRule {
  /** Path the rule applies to. */
  subfilter_replace_path: string;
  /** Search Content (the string/regex to match). */
  subfilter_string_in: string;
  /** Regex flag: "1" when the search content is a regex, "0" for a plain string. */
  subfilter_is_regex: string;
  /** Replacement string. */
  subfilter_string_out: string;
  /** Description. */
  subfilter_comment: string;
}

/** Access-log collection settings. Not surfaced as a tab yet; round-tripped. */
export interface LogSetting {
  collection_mode: string;
  col_response_body: boolean;
  time_per_second: number;
  enabled: boolean;
}

/** The account global-config object (the `config` slice of the record). */
export interface GlobalConfig {
  src_ip_strategy: SrcIpStrategy;
  enable_strictly_match_host: boolean;
  good_bots: GoodBots;
  error_page: ErrorPageEntry[];
  direct_connection_control: DirectConnectionControl;
  /** Web Access Control → IP Blacklist enable. */
  enable_black_list: boolean;
  inspection_setting: InspectionSetting;
  response_filtering: ResponseFilteringRule[];
  custom_errpage_status: string;
  log_setting: LogSetting;
  /** HTTP Settings → Maximum File Size (MB). */
  upload_max_size_num: number;
}

/** The whole global-config record (round-tripped verbatim on save). */
export interface GlobalConfigRecord {
  id: number;
  config: GlobalConfig;
  created: number;
  updated: number;
  status: number;
  customer_id: number;
}

// ---- msiEmail DTO --------------------------------------------------------

/** Monthly Security Report email config (the "Monthly Security Report" tab).
 *  Saved via POST /msiEmail (create-or-replace; `id` 0 = new). The vendor wire
 *  shape is `{id, enable, email[]}` — note the singular field names. */
export interface MsiEmail {
  /** Record id; 0 when never configured (the GET returns null in that case). */
  id: number;
  enable: boolean;
  /** Recipient email addresses (the textarea, one per line). */
  email: string[];
}

// ---- customer/solution/detail DTO ----------------------------------------

export interface SolutionAllowance {
  name: string; // domain | qps | volume
  quantity: number;
  unit: string; // num | QPS | GB
}

/** Subscription package + allowances (read-only). */
export interface SolutionDetail {
  subscriptionId: number;
  solutionId: number;
  packageId: number;
  startTimeMillis: number;
  endTimeMillis: number;
  packageName: string;
  allowances: SolutionAllowance[];
}

// ---- Paths + query keys --------------------------------------------------

function settingsPath(domainId: string, area: string): string {
  return `/waap/domains/${domainId}/${area}`;
}

export const settingsKeys = {
  globalConfig: (domainId: string) => ['waap', 'settings', domainId, 'global-config'] as const,
  msiEmail: (domainId: string) => ['waap', 'settings', domainId, 'msi-email'] as const,
  solutionDetail: (domainId: string) => ['waap', 'settings', domainId, 'solution-detail'] as const,
};

/** Patch the `config` slice of a record without mutating the original (for the
 *  whole-object round-trip: merge a tab's edits, then PUT the result). */
export function withConfig(
  record: GlobalConfigRecord,
  patch: Partial<GlobalConfig>,
): GlobalConfigRecord {
  return { ...record, config: { ...record.config, ...patch } };
}

// ---- Hooks ---------------------------------------------------------------

/** The account global-config record (whole object). null when unconfigured. */
export function useGlobalConfig(domainId: string | undefined) {
  return useQuery({
    queryKey: settingsKeys.globalConfig(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<GlobalConfigRecord | null> => {
      const res = await serviceApiV3.get<GlobalConfigRecord | null>(
        settingsPath(domainId as string, 'global-config'),
      );
      return res.data ?? null;
    },
  });
}

/** Save the whole global-config record (PUT). Invalidates on success. */
export function useUpdateGlobalConfig(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, GlobalConfigRecord>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: GlobalConfigRecord) => {
      const res = await serviceApiV3.put(settingsPath(domainId, 'global-config'), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: settingsKeys.globalConfig(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Monthly Security Report email config. null when never configured. */
export function useMsiEmail(domainId: string | undefined) {
  return useQuery({
    queryKey: settingsKeys.msiEmail(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<MsiEmail | null> => {
      const res = await serviceApiV3.get<MsiEmail | null>(
        settingsPath(domainId as string, 'msi-email'),
      );
      return res.data ?? null;
    },
  });
}

/** Save the Monthly Security Report email config (POST — create-or-replace). */
export function useUpdateMsiEmail(
  domainId: string,
  options?: UseMutationOptions<unknown, unknown, MsiEmail>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MsiEmail) => {
      const res = await serviceApiV3.post(settingsPath(domainId, 'msi-email'), body);
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: settingsKeys.msiEmail(domainId) });
      options?.onSuccess?.(...args);
    },
  });
}

/** Vendor result of an on-demand Monthly Security Report generation. The endpoint
 *  can return success:true yet still fail internally (results.error set + zero
 *  customers) when the vendor's own upstream is down — callers surface both. */
export interface MsiReportResult {
  success?: boolean;
  log?: string;
  results?: {
    month?: number;
    year?: number;
    total_customers?: number;
    success_customers?: string[];
    failed_customers?: string[];
    error?: string;
    [k: string]: unknown;
  };
}

/** Trigger on-demand Monthly Security Report generation + email for a month
 *  ("YYYY-MM"). Returns the vendor's raw result. */
export function useSendMsiReport(
  domainId: string,
  options?: UseMutationOptions<MsiReportResult, unknown, string>,
) {
  return useMutation({
    mutationFn: async (yearMonth: string): Promise<MsiReportResult> => {
      const [year, month] = yearMonth.split('-');
      const res = await serviceApiV3.post<MsiReportResult>(
        settingsPath(domainId, 'msi-report'),
        { month, year },
      );
      return res.data;
    },
    ...options,
  });
}

/** Subscription package + allowances (read-only). null when unavailable. */
export function useSolutionDetail(domainId: string | undefined) {
  return useQuery({
    queryKey: settingsKeys.solutionDetail(domainId ?? ''),
    enabled: !!domainId,
    retry: false,
    queryFn: async (): Promise<SolutionDetail | null> => {
      const res = await serviceApiV3.get<SolutionDetail | null>(
        settingsPath(domainId as string, 'solution-detail'),
      );
      return res.data ?? null;
    },
  });
}

// ---- Subscription package (change) ---------------------------------------

/** Per-plan feature entitlements. Only the features that vary by tier are
 *  tracked here; everything else (AI-WAF, DDoS, Emergency Mitigation, CDN,
 *  admin) is available on every plan. */
export interface WaapPlanFeatures {
  botManagement: boolean;
  apiProtection: boolean;
  programmableMitigation: boolean;
}

/** A selectable WAAP subscription plan (the vendor's MaxiSafe tiers). */
export interface WaapPlan {
  /** Vendor package id — sent as `package_id`. */
  id: string;
  name: string;
  price: string;
  /** Monthly price in USD (0 for Free). Mirrors the server-authoritative price
   *  map in `orders.upgrade.controllers.go`; used to prefill a top-up. */
  priceUsd: number;
  domains: string;
  volume: string;
  qps: string;
  features: WaapPlanFeatures;
}

/** USD→VND rate for a plan charge — mirrors `waapPlanVNDRate` on the backend.
 *  Keep in sync; the backend is the source of truth for the actual charge. */
export const WAAP_PLAN_VND_RATE = 25_000;

/** The monthly VND charge for a WAAP package id (0 when Free/unknown). Used to
 *  prefill the top-up amount when the wallet can't cover an upgrade. */
export function waapPlanMonthlyVnd(packageId: string): number {
  const plan = WAAP_PLANS.find((p) => p.id === String(packageId));
  return plan ? plan.priceUsd * WAAP_PLAN_VND_RATE : 0;
}

const BASE_FEATURES: WaapPlanFeatures = {
  botManagement: false,
  apiProtection: false,
  programmableMitigation: false,
};
const FULL_FEATURES: WaapPlanFeatures = {
  botManagement: true,
  apiProtection: true,
  programmableMitigation: true,
};

/**
 * The self-serve WAAP plans and their entitlements. Bot Management, API
 * Protection and Programmable Mitigation are only included from Pro up; the
 * lower tiers (Free, Standard) get AI-WAF / DDoS / Emergency Mitigation only.
 *
 * Pro maps to the vendor's top MaxiSafe package (id 4004); the vendor's mid tier
 * (4003) is not offered here. Custom/Enterprise is a contact-sales tier (no
 * package id — see PackageSettings). Verify the id → entitlement mapping with the
 * vendor if gating ever looks off.
 */
export const WAAP_PLANS: WaapPlan[] = [
  { id: '4001', name: 'Free', price: '', priceUsd: 0, domains: '1', volume: '1 TB', qps: '100', features: BASE_FEATURES },
  { id: '4002', name: 'Standard', price: '$20/mo', priceUsd: 20, domains: 'Up to 3', volume: '2 TB', qps: '300', features: BASE_FEATURES },
  { id: '4004', name: 'Pro', price: '$200/mo', priceUsd: 200, domains: 'Up to 12', volume: '5 TB', qps: '1,200', features: FULL_FEATURES },
];

/** Feature entitlements for a package id. An unknown / unreadable / absent
 *  package resolves to permissive (all features) so we never wrongly hide a
 *  feature a customer paid for when the plan can't be determined. */
export function waapPlanFeatures(
  packageId: number | string | undefined | null,
): WaapPlanFeatures {
  if (packageId === undefined || packageId === null || packageId === '') return FULL_FEATURES;
  const plan = WAAP_PLANS.find((p) => p.id === String(packageId));
  return plan ? plan.features : FULL_FEATURES;
}

/** The service's active subscription package id (authoritative, service-scoped:
 *  GET /services/:serviceId/waap/package → the vendor's checkedPackageId). '' when
 *  no package is set. Works even before any website exists. */
export function useWaapCurrentPackage(serviceId: string | undefined) {
  return useQuery({
    queryKey: ['waap', 'service', serviceId ?? '', 'package'],
    enabled: !!serviceId,
    retry: false,
    queryFn: async (): Promise<string> => {
      const res = await serviceApiV3.get<{ package_id?: string }>(
        `/services/${serviceId}/waap/package`,
      );
      return res.data?.package_id ?? '';
    },
  });
}

/**
 * Bill a WAAP plan upgrade to the customer's credit wallet BEFORE the vendor
 * package swap (DataCentral `POST /partner/orders/upgrade`). The plan price is
 * server-authoritative (only the package id is sent). On success the wallet is
 * charged for the month and the service is marked recurring; a 402 means the
 * wallet can't cover it — the caller prompts a top-up then retries.
 */
export function useUpgradeWaapPlanBilling(serviceId: string) {
  return useMutation({
    mutationFn: async (packageId: string) => {
      const res = await dataCentralApi.post('/partner/orders/upgrade', {
        service_id: serviceId,
        package_id: packageId,
      });
      return res.data;
    },
  });
}

/** True when an upgrade-billing error is a 402 (insufficient credit). */
export function isInsufficientCreditError(err: unknown): boolean {
  return (err as { response?: { status?: number } })?.response?.status === 402;
}

/** Change (or clear, with `package_id: ''`) a WAAP service's subscription
 *  package. Service-scoped: PUT /services/:serviceId/waap/package. */
export function useChangeWaapPackage(
  serviceId: string,
  options?: UseMutationOptions<unknown, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (packageId: string) => {
      const res = await serviceApiV3.put(`/services/${serviceId}/waap/package`, {
        package_id: packageId,
      });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      // Refresh the active-package read (service-scoped) and the per-website
      // solution-detail so the displayed plan updates everywhere.
      qc.invalidateQueries({ queryKey: ['waap', 'service'] });
      qc.invalidateQueries({ queryKey: ['waap', 'settings'] });
      options?.onSuccess?.(...args);
    },
  });
}
